import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Dialog,
  Button,
  MobileStepper,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Grow,
  IconButton,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import {
  Users,
  User,
  GraduationCap,
  Radio,
  Mic,
  Smartphone,
  Calendar,
  ArrowLeft,
  ArrowRight,
  Youtube,
  Tv,
  Chrome,
  Link,
  CloudUpload,
  Blocks,
} from 'lucide-mui';
import type { GlobalDialogProps, PackageType } from '../../../../types';
import UpgradeView from '../../../plan/upgrade/upgradeView';
import useUserTags from '../../../../hooks/useTags';
import CalendarConnectionScreen from './CalendarConnectionScreen';
import ChromeExtensionScreen from './ChromeExtensionScreen';
import RecordFromPhoneScreen from './RecordFromPhoneScreen';

const ANIMATION_DURATION = 400;
export const ONBOARDING_STEP_TAG_VALUE = '2024-Dec';

const importOptions = [
  {
    value: 'import-from-url',
    icon: Link,
    label: 'importFromUrl',
  },
  {
    value: 'import-from-youtube',
    icon: Youtube,
    label: 'importFromYoutube',
  },
  {
    value: 'record-screen',
    icon: Radio,
    label: 'recordScreen',
  },
  { value: 'record-audio', icon: Mic, label: 'recordAudio' },
  {
    value: 'join-meeting',
    icon: Calendar,
    label: 'joinMeeting',
  },
  { value: 'record-phone', icon: Smartphone, label: 'recordPhone' },
  {
    value: 'chrome-extension',
    icon: Chrome,
    label: 'chromeExtension',
  },
  {
    value: 'record-livestream',
    icon: Tv,
    label: 'recordLivestream',
  },
  { value: 'local-files', icon: CloudUpload, label: 'localFiles' },
  {
    value: 'video-library',
    icon: Blocks,
    label: 'videoLibrary',
  },
];

const goalOptions = [
  {
    icon: <User sx={{ fontSize: 48, mb: 2 }} />,
    title: 'personal',
    description: 'personal',
    tagValue: 'personal',
  },
  {
    icon: <Users sx={{ fontSize: 48, mb: 2 }} />,
    title: 'business',
    description: 'business',
    tagValue: 'business',
  },
  {
    icon: <GraduationCap sx={{ fontSize: 48, mb: 2 }} />,
    title: 'education',
    description: 'education',
    tagValue: 'education',
  },
];

const commonButtonStyles = {
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: 'none',
  borderRadius: '16px',
  backgroundColor: 'background.default',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: 'none',
    backgroundColor: 'action.hover',
  },
};

const nextButtonStyles = {
  minWidth: 200,
  textTransform: 'none',
  fontSize: '1.1rem',
  mb: 2,
  position: 'sticky',
  bottom: 16,
  zIndex: 1,
};

interface FileType {
  _id: string;
  parentId: string | null;
  streams?: string[];
}

interface AppState {
  library: {
    files: {
      data: FileType[];
    };
  };
  team: {
    selectedTeam?: {
      teamId: string;
    };
  };
  import: {
    status: string;
  };
  plan: {
    subscription: {
      package: PackageType;
    };
  };
}

type ComponentId = string;
type AdditionalScreenComponent = React.FC<{
  onClose?: GlobalDialogProps['onClose'];
}>;

// Component registry for additional onboarding screens
const additionalScreens: Record<ComponentId, AdditionalScreenComponent> = {
  'calendar-connection': CalendarConnectionScreen,
  'chrome-extension': ChromeExtensionScreen,
  'record-from-phone': RecordFromPhoneScreen,
};

export const OnboardingModal = ({ onClose }: GlobalDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getTag, setTag } = useUserTags();
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedImports, setSelectedImports] = useState<string[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<string | null>(null);

  const files = useSelector((state: AppState) => state.library.files.data);
  const currentTeamId = useSelector(
    (state: AppState) => state.team.selectedTeam?.teamId
  );
  const importStatus = useSelector((state: AppState) => state.import.status);
  const currentPackage = useSelector(
    (state: AppState) => state.plan.subscription.package
  );

  const showPaymentStep = currentPackage === 'FREE_LOGGEDIN';

  // Determine if we should show additional screens based on selected imports
  const additionalScreensToShow = useMemo(() => {
    const screens: ComponentId[] = [];

    // If chrome-extension is selected, show the Chrome extension screen
    if (selectedImports.includes('chrome-extension')) {
      const extensionInstalled = getTag('chromeExtensionInstalled') === 'true';
      if (!extensionInstalled) {
        screens.push('chrome-extension');
      }
    }

    // If record-phone is selected, show the mobile app screen
    if (selectedImports.includes('record-phone')) {
      const mobileAppInstalled = getTag('mobileAppInstalled') === 'true';
      if (!mobileAppInstalled) {
        screens.push('record-from-phone');
      }
    }

    // If the join-meeting option is selected, show the calendar connection screen
    // (Push this LAST to ensure it comes at the end)
    if (selectedImports.includes('join-meeting')) {
      // Check if user already has calendar connected
      const calendarConnected = getTag('calendarConnected') === 'true';
      if (!calendarConnected) {
        screens.push('calendar-connection');
      }
    }

    return screens;
  }, [selectedImports, getTag]);

  // Build the full array of steps including dynamic ones
  const steps = useMemo(() => {
    let baseSteps = ['Welcome', 'Your Goals', 'Import Methods'];

    // Add dynamic steps based on selected options
    if (additionalScreensToShow.length > 0) {
      baseSteps = [
        ...baseSteps,
        ...additionalScreensToShow.map((screen) => {
          if (screen === 'calendar-connection')
            return t('onboarding.additionalSteps.connectCalendar');
          if (screen === 'chrome-extension')
            return t('onboarding.additionalSteps.chromeExtension');
          if (screen === 'record-from-phone')
            return t('onboarding.additionalSteps.mobileApp');
          return screen; // Default fallback
        }),
      ];
    }

    // Add payment step if needed
    if (showPaymentStep) {
      baseSteps.push('Choose a Plan');
    }

    return baseSteps;
  }, [additionalScreensToShow, showPaymentStep, t]);

  const handleClose = () => {
    if (selectedImports.length > 0) {
      dispatch({
        type: 'onboarding/trackImportOptions',
        payload: { importOptions: selectedImports },
      });

      setTag({
        key: 'userImportMethods',
        value: selectedImports.join(','),
      });
    }

    setTag({
      key: 'onboardingStep',
      value: ONBOARDING_STEP_TAG_VALUE,
    });

    if (onClose) {
      onClose();
    }

    const firstVideo = files.find(
      (file: FileType) =>
        file.streams?.includes('video') || file.streams?.includes('audio')
    );

    if (firstVideo && currentTeamId) {
      const parentId = firstVideo.parentId || 'default';
      navigate(`/library/${currentTeamId}/${parentId}/${firstVideo._id}`);
    }
  };

  const handleImportToggle = (value: string) => {
    setSelectedImports((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      }
      return [...prev, value];
    });
  };

  const handleGoalSelect = (goalValue: string) => {
    setSelectedGoal(goalValue);
    dispatch({
      type: 'onboarding/trackGoal',
      payload: { goal: goalValue },
    });
    setTag({
      key: 'userGoal',
      value: goalValue,
    });
    setActiveStep((prev) => prev + 1);
  };

  const renderStepContent = (step: number) => {
    if (step === 0) {
      return (
        <Fade in timeout={ANIMATION_DURATION}>
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%',
              minHeight: '400px',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 300, mb: 2 }}>
              {importStatus === 'succeeded' || importStatus === 'pending'
                ? t('onboarding.welcome.readyTitle')
                : t('onboarding.welcome.title')}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 3, textAlign: 'center' }}
            >
              {importStatus === 'succeeded' || importStatus === 'pending'
                ? t('onboarding.welcome.readyDescription')
                : t('onboarding.welcome.description')}
            </Typography>
            <Box
              sx={{
                width: '70%',
                maxWidth: '800px',
                position: 'relative',
                margin: '0 auto',
                '&::before': {
                  content: '""',
                  display: 'block',
                  paddingTop: '56.25%',
                },
              }}
            >
              <Box
                component="iframe"
                src="https://www.youtube.com/embed/a3L4K7Y8E54?autoplay=0&rel=0&modestbranding=1"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title="ScreenApp Demo"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                  borderRadius: 1,
                }}
              />
            </Box>
          </Box>
        </Fade>
      );
    }

    if (step === 1) {
      return (
        <Fade in timeout={ANIMATION_DURATION}>
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              mb: 4,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 300, mb: 2 }}>
              {t('onboarding.goals.title')}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 4, textAlign: 'center' }}
            >
              {t('onboarding.goals.description')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1.5 : 3,
                maxWidth: '1000px',
                margin: '0 auto',
                flex: 1,
                alignItems: 'center',
              }}
            >
              {goalOptions.map((option, index) => (
                <Grow
                  key={option.title}
                  in
                  timeout={ANIMATION_DURATION + index * 200}
                >
                  <Button
                    variant="text"
                    onClick={() => handleGoalSelect(option.tagValue)}
                    sx={{
                      ...commonButtonStyles,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: isMobile ? 1.5 : 3,
                      textAlign: 'center',
                      minHeight: {
                        xs: '140px',
                        sm: '160px',
                        md: '250px',
                      },
                      width: '100%',
                      backgroundColor:
                        selectedGoal === option.tagValue
                          ? 'secondary.main'
                          : 'background.default',
                      color:
                        selectedGoal === option.tagValue
                          ? 'common.white'
                          : 'inherit',
                      '&:hover': {
                        ...commonButtonStyles['&:hover'],
                        backgroundColor:
                          selectedGoal === option.tagValue
                            ? 'secondary.dark'
                            : 'action.hover',
                      },
                    }}
                  >
                    {React.cloneElement(option.icon, {
                      sx: {
                        fontSize: {
                          xs: 32,
                          sm: 36,
                          md: 48,
                        },
                        mb: {
                          xs: 0.5,
                          sm: 1,
                          md: 2,
                        },
                      },
                    })}
                    <Typography
                      variant={isMobile ? 'body1' : 'h6'}
                      sx={{
                        mb: { xs: 0.25, sm: 0.5 },
                        fontSize: {
                          xs: '0.9rem',
                          sm: 'inherit',
                        },
                      }}
                    >
                      {t(`onboarding.goals.options.${option.title}.title`)}
                    </Typography>
                    <Typography
                      variant={isMobile ? 'body2' : 'body1'}
                      color="text.secondary"
                      sx={{
                        fontSize: {
                          xs: '0.8rem',
                          sm: 'inherit',
                        },
                      }}
                    >
                      {t(
                        `onboarding.goals.options.${option.description}.description`
                      )}
                    </Typography>
                  </Button>
                </Grow>
              ))}
            </Box>
          </Box>
        </Fade>
      );
    }

    if (step === 2) {
      return (
        <Fade in timeout={ANIMATION_DURATION}>
          <Box sx={{ width: '100%', textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 300, mb: 2 }}>
              {t('onboarding.importMethods.title')}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 4, textAlign: 'center' }}
            >
              {t('onboarding.importMethods.description')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '1fr 1fr',
                },
                gap: 2,
                maxWidth: '1000px',
                margin: '0 auto',
                maxHeight: isMobile ? 'calc(100vh - 300px)' : 'none',
                overflowY: isMobile ? 'auto' : 'visible',
                px: 2,
              }}
            >
              {importOptions.map((option, index) => {
                const Icon = option.icon;
                return (
                  <Grow
                    key={option.value}
                    in
                    timeout={ANIMATION_DURATION + index * 200}
                  >
                    <Button
                      variant="text"
                      onClick={() => handleImportToggle(option.value)}
                      sx={{
                        ...commonButtonStyles,
                        height: isMobile ? '48px' : '60px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        padding: isMobile ? 2 : 3,
                        textAlign: 'left',
                        gap: 2,
                        backgroundColor: selectedImports.includes(option.value)
                          ? 'secondary.main'
                          : 'background.default',
                        color: selectedImports.includes(option.value)
                          ? 'common.white'
                          : 'inherit',
                        '&:hover': {
                          ...commonButtonStyles['&:hover'],
                          backgroundColor: selectedImports.includes(
                            option.value
                          )
                            ? 'secondary.dark'
                            : 'action.hover',
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: isMobile ? 24 : 32 }} />
                      <Typography variant={isMobile ? 'body1' : 'h6'}>
                        {t(`onboarding.importMethods.options.${option.label}`)}
                      </Typography>
                    </Button>
                  </Grow>
                );
              })}
            </Box>
          </Box>
        </Fade>
      );
    }

    // Handle dynamic additional screens
    const dynamicScreenOffset = 3; // Base screens count
    const additionalScreenIndex = step - dynamicScreenOffset;

    if (
      additionalScreenIndex >= 0 &&
      additionalScreenIndex < additionalScreensToShow.length
    ) {
      const screenId = additionalScreensToShow[additionalScreenIndex];
      const ComponentToRender = additionalScreens[screenId];

      // Pass onClose prop to CalendarConnectionScreen
      if (screenId === 'calendar-connection') {
        return <ComponentToRender onClose={onClose} />;
      }

      return <ComponentToRender />;
    }

    // Payment step (last step if needed)
    const paymentStepIndex =
      dynamicScreenOffset + additionalScreensToShow.length;
    if (showPaymentStep && step === paymentStepIndex) {
      return (
        <Fade in timeout={ANIMATION_DURATION}>
          <Box>
            <Typography
              variant="h4"
              sx={{ textAlign: 'center', fontWeight: 300 }}
            >
              {t('onboarding.payment.title')}
            </Typography>
            <Slide direction="up" in timeout={ANIMATION_DURATION}>
              <Box>
                <UpgradeView />
              </Box>
            </Slide>
          </Box>
        </Fade>
      );
    }

    return null;
  };

  const SlideTransition = React.useMemo(
    () =>
      React.forwardRef(function TransitionComponent(
        props: TransitionProps & {
          children: React.ReactElement;
        },
        ref: React.Ref<unknown>
      ) {
        return isMobile ? (
          <Slide direction="up" ref={ref} {...props} />
        ) : (
          <Fade ref={ref} {...props} timeout={ANIMATION_DURATION} />
        );
      }),
    [isMobile]
  );

  // Function to handle next button action based on current step
  const handleNext = () => {
    const currentStepIndex = activeStep;
    const totalSteps = steps.length;
    const isLastStep = currentStepIndex === totalSteps - 1;
    const isImportMethodsStep = currentStepIndex === 2;

    // Special handling for the import methods step
    if (isImportMethodsStep) {
      if (additionalScreensToShow.length > 0) {
        // If there are additional screens to show, proceed to the next step
        setActiveStep(currentStepIndex + 1);
      } else if (!showPaymentStep) {
        // If no payment step and no additional screens, close the modal
        handleClose();
      } else {
        // Otherwise proceed to next step (payment or another additional screen)
        setActiveStep(currentStepIndex + 1);
      }
    } else if (isLastStep) {
      // If we're on the last step, close the modal
      handleClose();
    } else {
      // In all other cases, go to next step
      setActiveStep(currentStepIndex + 1);
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={SlideTransition}
      PaperProps={{
        sx: {
          m: isMobile ? 0 : 2,
          borderRadius: isMobile ? 0 : 4,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          transition: 'all 0.3s ease-in-out',
          maxHeight: isMobile ? '100vh' : '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 2, sm: 4 },
            position: 'relative',
          }}
        >
          <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
            {activeStep > 0 ? (
              <IconButton
                onClick={() => setActiveStep((prev) => prev - 1)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                  },
                }}
              >
                <ArrowLeft />
              </IconButton>
            ) : (
              <Box sx={{ width: 40 }} />
            )}
          </Box>
          <MobileStepper
            variant="dots"
            steps={steps.length}
            position="static"
            activeStep={activeStep}
            sx={{
              height: '40px',
              background: 'transparent',
              flex: 1,
              justifyContent: 'center',
              padding: 0,
              '& .MuiMobileStepper-dots': {
                gap: '8px',
              },
              '& .MuiMobileStepper-dot': {
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                margin: 0,
                '&.MuiMobileStepper-dotActive': {
                  backgroundColor: 'primary.main',
                  transform: 'scale(1.2)',
                },
              },
            }}
            nextButton={null}
            backButton={null}
            onClick={(e) => {
              const dots = e.currentTarget.querySelectorAll(
                '.MuiMobileStepper-dot'
              );
              const clickedDotIndex = Array.from(dots).indexOf(
                e.target as Element
              );
              if (clickedDotIndex !== -1) {
                setActiveStep(clickedDotIndex);
              }
            }}
          />
          {showPaymentStep && activeStep === steps.length - 1 && (
            <Button
              onClick={handleClose}
              color="inherit"
              sx={{
                position: 'absolute',
                right: 0,
                textTransform: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              {t('common.later')}
            </Button>
          )}
          <Box sx={{ width: 40 }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pb: {
              xs:
                activeStep < steps.length - 1 &&
                activeStep !== 1 &&
                (activeStep !== 2 || selectedImports.length > 0)
                  ? 2
                  : 0,
              sm: 4,
            },
          }}
        >
          {renderStepContent(activeStep)}
        </Box>

        {activeStep < steps.length - 1 &&
          activeStep !== 1 &&
          (activeStep !== 2 || selectedImports.length > 0) && (
            <Fade in timeout={ANIMATION_DURATION}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'background.paper',
                  position: 'sticky',
                  bottom: 0,
                  py: { xs: 1.5, sm: 2 },
                }}
              >
                <Button
                  onClick={handleNext}
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={activeStep === 2 && selectedImports.length === 0}
                  sx={{
                    ...nextButtonStyles,
                    mb: 0,
                  }}
                  endIcon={<ArrowRight />}
                >
                  {activeStep === steps.length - 2
                    ? t('common.done')
                    : t('common.next')}
                </Button>
              </Box>
            </Fade>
          )}
      </Box>
    </Dialog>
  );
};

export default OnboardingModal;
